import { useState, useEffect, useRef } from "react";
import toast from "react-hot-toast";
import { formatGroupedNumber } from "../../utils/currencyFormatter";

import DetailedPositionsControl, {
  DETAILED_POSITION_OPTIONS,
} from "../common/DetailedPositionsControl";
import { useNavigate } from "react-router-dom";
import { clubService } from "../../services/api";
import NationalityFlags from "../common/NationalityFlags";

const PlayerCard = ({
  player,
  isQuickEdit,
  canEditSquad,
  onAddToSale,
  onAddToLoan,
  onRemoveFromSale,
  onRemoveFromLoan,
  onStatusChange,
  saleAmount,
  loanAmount,
  onDetailedPositionsSave,
  onContractExpirySave,
  onPhotoUpload,
  onPlayerRemoved,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [uiState, setUiState] = useState("initial"); // "initial", "show-add-fee-button", "show-input", "show-fee-display", "show-marked-available"
  const [editType, setEditType] = useState(""); // "sale" or "loan"
  const [selectedStatus, setSelectedStatus] = useState("");
  const [isCardCancelled, setIsCardCancelled] = useState(false); // New state for card cancellation
  const [isDropdownOpen, setIsDropdownOpen] = useState(false); // For custom dropdown
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Initialize state for players with existing fees
  useEffect(() => {
    if (isQuickEdit && (player.forSale || player.forLoan)) {
      if (player.forSale && player.transferFee) {
        setEditType("sale");
        setUiState("show-fee-display");
      } else if (player.forLoan && player.loanFee) {
        setEditType("loan");
        setUiState("show-fee-display");
      } else {
        // Player has status but no fee - shouldn't happen but handle it
        setUiState("initial");
      }
    }
  }, [
    isQuickEdit,
    player.forSale,
    player.forLoan,
    player.transferFee,
    player.loanFee,
  ]);

  // Reset card cancellation state when quick edit changes or player changes
  useEffect(() => {
    setIsCardCancelled(false);
  }, [isQuickEdit, player.id]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Get detailed positions from player data
  const detailedPositions =
    player.tp_detailed_position || player.tpDetailedPosition || {};

  // Helper function to get position label from code
  const getPositionLabel = (positionCode) => {
    if (!positionCode) return "";
    const position = DETAILED_POSITION_OPTIONS.find(
      (opt) => opt.value.toLowerCase() === positionCode.toLowerCase()
    );
    return position ? position.label : positionCode.toUpperCase();
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
  };

  const handleAddToSaleClick = () => {
    setIsCardCancelled(false);
    setEditType("sale");
    setUiState("show-add-fee-button");
  };

  const handleAddToLoanClick = () => {
    setIsCardCancelled(false);
    setEditType("loan");
    setUiState("show-add-fee-button");
  };

  const handleAddFeeButtonClick = () => {
    // Load existing fee value if available
    const existingFee =
      editType === "sale"
        ? saleAmount || player.transferFee?.toString() || ""
        : loanAmount || player.loanFee?.toString() || "";
    setIsCardCancelled(false);
    setInputValue(existingFee);
    setUiState("show-input");
  };

  const handleConfirmFee = () => {
    if (inputValue.trim()) {
      // Just update local state, don't call API
      if (editType === "sale") {
        onAddToSale(player.id, inputValue);
        // Remove from loan if it was there
        onRemoveFromLoan(player.id);
      } else if (editType === "loan") {
        onAddToLoan(player.id, inputValue);
        // Remove from sale if it was there
        onRemoveFromSale(player.id);
      }
      setIsCardCancelled(false);
      setUiState("show-fee-display");
    }
  };

  const handleCancelInput = () => {
    setInputValue("");
    // If player already has a status, go back to initial (dropdown + edit button)
    // Otherwise, mark as cancelled to show two buttons
    if (player.forSale || player.forLoan) {
      setIsCardCancelled(false);
      setUiState("initial");
      setSelectedStatus(""); // Reset dropdown to original value
    } else {
      setIsCardCancelled(true);
      setUiState("initial");
      setEditType("");
    }
  };

  const handleEditFee = () => {
    // Load existing fee value - from local changes or from player object
    const existingFee =
      editType === "sale"
        ? saleAmount || player.transferFee?.toString() || ""
        : loanAmount || player.loanFee?.toString() || "";
    setIsCardCancelled(false);
    setInputValue(existingFee);
    setUiState("show-input");
  };

  const handleStatusDropdownChange = (newStatus) => {
    setIsCardCancelled(false);
    setSelectedStatus(newStatus);
    setIsDropdownOpen(false);
    if (newStatus === "available") {
      setEditType("available");
      setUiState("show-status-actions");
    } else {
      // When changing status from sale to loan or vice versa
      setEditType(newStatus);
      // If changing status, clear the input and show the "add fee button" state
      setInputValue("");
      setUiState("show-add-fee-button");
    }
  };

  const getButtonLabel = () => {
    // Get current selected status from dropdown or player's original status
    const currentStatus =
      selectedStatus ||
      (player.forSale ? "sale" : player.forLoan ? "loan" : editType);

    // If player has existing status
    if (player.forSale || player.forLoan) {
      // Check if user selected the SAME status as original
      if (currentStatus === "sale" && player.forSale) {
        return "Edit transfer fee";
      } else if (currentStatus === "loan" && player.forLoan) {
        return "Edit loan fee";
      } else if (currentStatus === "sale") {
        return "Add Transfer fee";
      } else if (currentStatus === "loan") {
        return "Add Loan fee";
      }
    }

    // For new additions (players without status)
    return currentStatus === "sale"
      ? "Add Max. Transfer fee"
      : "Add Max. Loan fee";
  };

  const handleConfirmStatusChange = () => {
    if (selectedStatus === "available") {
      onStatusChange(player.id, "not_available", null);
      setIsCardCancelled(false);
      setUiState("show-marked-available");
      setEditType("");
    }
  };

  const handleCancelStatusChange = () => {
    setSelectedStatus(""); // Reset to original
    setUiState("initial");
    setEditType("");
  };

  const handleEditMarkedAvailable = () => {
    setIsCardCancelled(false);
    setSelectedStatus("");
    setUiState("initial");
  };

  const handleDetailedPositionsSave = async (payload) => {
    if (onDetailedPositionsSave) {
      await onDetailedPositionsSave(payload);
    }
  };

  const handleContractExpirySave = async (payload) => {
    if (!onContractExpirySave) {
      toast.error("Contract expiry save handler not available");
      throw new Error("Contract expiry save handler not available");
    }

    try {
      await onContractExpirySave(player, payload);
    } catch (error) {
      // Error handling is done in the parent component
      throw error;
    }
  };

  const handleRemoveFromSquad = async (playerToRemove, reason) => {
    try {
      // Prepare the payload for API
      const playerPayload = {
        wyscout_player_id: playerToRemove.wyscoutPlayerId,
        reason: reason || "Removed from squad by club admin",
      };

      // Call the API
      const response = await clubService.removePlayerFromSquad(playerPayload);

      if (response.success) {
        toast.success(`${playerToRemove.name} has been removed from the squad`);

        // Call parent callback to update the players list
        if (onPlayerRemoved) {
          onPlayerRemoved(playerToRemove.id || playerToRemove.playerId);
        }
      } else {
        toast.error(response.error || "Failed to remove player from squad");
        throw new Error(response.error);
      }
    } catch (error) {
      console.error("Error removing player from squad:", error);
      toast.error("Failed to remove player from squad");
      throw error;
    }
  };

  return (
    <div className="bg-white rounded-lg p-6 hover:shadow transition-shadow border border-[#D2D4D880] h-full flex flex-col">
      <div className="flex flex-col items-start gap-4 mb-4">
        <div className="flex justify-between w-full">
          {player.image ? (
            <img
              onClick={() => {
                navigate(`/search/player/${player.id}`);
              }}
              src={player.image}
              alt={player.name}
              className="cursor-pointer w-16 h-16 rounded-lg object-cover"
              onError={(e) => {
                e.target.style.display = "none";
                // e.target.nextSibling.style.display = "flex";
              }}
            />
          ) : (
            <div
              onClick={() => {
                navigate(`/search/player/${player.id}`);
              }}
              className="cursor-pointer w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center text-xl font-bold text-gray-600"
            >
              {player.name?.charAt(0) || "P"}
            </div>
          )}

          <div className="flex items-start gap-2">
            {!isQuickEdit && (
              <div className="flex flex-col items-end gap-1">
                {player.forSale && (
                  <span className="px-2 py-1 text-xs font-medium text-white bg-[#049669] rounded h-fit">
                    FOR SALE
                  </span>
                )}
                {player.forLoan && (
                  <span className="px-2 py-1 text-xs font-medium text-white bg-[#FF7500] rounded h-fit">
                    FOR LOAN
                  </span>
                )}
              </div>
            )}

            {canEditSquad && (
              <DetailedPositionsControl
                player={player}
                onSave={handleDetailedPositionsSave}
                onContractExpirySave={handleContractExpirySave}
                onPhotoUpload={onPhotoUpload}
                onRemoveFromSquad={handleRemoveFromSquad}
                isFromClub={true}
              />
            )}
          </div>
        </div>
        <div className="flex-1">
          <div className="flex items-start justify-between">
            <h3 className="text-xl font-bold text-[#242426]">{player.name}</h3>
          </div>
        </div>
      </div>

      <div className="flex bg-[#F7F7F7] p-2 rounded-lg mb-4 items-center gap-2 mt-1 justify-between w-full">
        <span className="text-sm text-[#1A212F] font-medium">
          {detailedPositions.primary_position
            ? `${getPositionLabel(detailedPositions.primary_position)} (${
                detailedPositions.primary_position
              })`
            : player.position}
        </span>
        {/* All citizenships, national team first (see NationalityFlags). */}
        <NationalityFlags
          nationalities={player}
          className="w-4 h-3 rounded"
          wrapperClassName="flex flex-wrap items-center gap-1.5"
        />
      </div>

      <div className="space-y-3 flex-grow">
        <div className="flex items-center justify-between">
          <p className="text-[13px] text-secondary">Primary Position</p>
          <p className="text-sm font-medium text-gray-900">
            {detailedPositions?.primary_position || "-"}
          </p>
        </div>

        <div className="flex items-center justify-between">
          <p className="text-[13px] text-secondary">Secondary Position</p>
          <p className="text-sm font-medium text-gray-900">
            {detailedPositions?.secondary_position || "-"}
          </p>
        </div>

        <div className="flex items-center justify-between">
          <p className="text-[13px] text-secondary">Tertiary Position</p>
          <p className="text-sm font-medium text-gray-900">
            {player.tertiary_position ||
              detailedPositions.teritory_position ||
              "-"}
          </p>
        </div>

        <div className="flex items-center justify-between">
          <p className="text-[13px] text-secondary">
            Games played in last season
          </p>
          <p className="text-sm font-medium text-gray-900">
            {player.gamesPlayed}
          </p>
        </div>
        <div className="flex items-center justify-between">
          <p className="text-[13px] text-secondary">
            Club contract expiring in
          </p>
          <p className="text-sm font-medium text-gray-900">
            {player.contractExpiry}
          </p>
        </div>
        <div className="flex items-center justify-between">
          <p className="text-[13px] text-secondary">Age</p>
          <p className="text-sm font-medium text-gray-900">{player.age}</p>
        </div>
        {/* Display existing fees when not in quick edit mode */}
        {!isQuickEdit && player.forSale && player.transferFee && (
          <div className="flex items-center justify-between">
            <p className="text-[13px] text-secondary">Transfer fee</p>
            <p className="text-sm font-medium text-gray-900">
              € {formatGroupedNumber(player.transferFee)}
            </p>
          </div>
        )}
        {!isQuickEdit && player.forLoan && player.loanFee && (
          <div className="flex items-center justify-between">
            <p className="text-[13px] text-secondary">Loan fee</p>
            <p className="text-sm font-medium text-gray-900">
              € {formatGroupedNumber(player.loanFee)}
            </p>
          </div>
        )}
      </div>

      {isQuickEdit && !isCardCancelled && (
        <div className="mt-4">
          {/* For players with existing status (forSale or forLoan) */}
          {player.forSale || player.forLoan ? (
            <>
              {/* Show appropriate UI based on state */}
              {uiState === "show-marked-available" ? (
                <button
                  onClick={handleEditMarkedAvailable}
                  className="w-full px-3 py-2 text-sm text-[#1E1E1E] border border-[#E6E6E6] rounded-lg hover:bg-gray-50 flex items-center justify-between"
                >
                  <span>Marked as available</span>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M11.3333 1.99955C11.5083 1.82445 11.7162 1.68556 11.945 1.5908C12.1738 1.49604 12.419 1.44727 12.6666 1.44727C12.9142 1.44727 13.1594 1.49604 13.3882 1.5908C13.617 1.68556 13.8248 1.82445 13.9999 1.99955C14.175 2.17465 14.3139 2.38252 14.4087 2.61129C14.5034 2.84006 14.5522 3.08526 14.5522 3.33288C14.5522 3.58051 14.5034 3.8257 14.4087 4.05448C14.3139 4.28325 14.175 4.49112 13.9999 4.66622L4.99992 13.6662L1.33325 14.6662L2.33325 10.9996L11.3333 1.99955Z"
                      stroke="#2C2C2C"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              ) : uiState === "show-status-actions" ? (
                <div className="flex items-center gap-2">
                  <div className="flex-1 relative" ref={dropdownRef}>
                    <button
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      className="w-full px-3 py-2 text-sm border border-[#E6E6E6] rounded-lg hover:bg-gray-50 flex items-center justify-between"
                    >
                      <span>
                        {selectedStatus === "available"
                          ? "Mark as available"
                          : selectedStatus === "sale"
                          ? "For Sale"
                          : selectedStatus === "loan"
                          ? "For Loan"
                          : player.forSale
                          ? "For Sale"
                          : "For Loan"}
                      </span>
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                    {isDropdownOpen && (
                      <div className="absolute top-full left-0 w-full mt-1 bg-white border border-[#E6E6E6] rounded-lg shadow-lg z-10">
                        <button
                          onClick={() => handleStatusDropdownChange("sale")}
                          className="w-full px-3 py-2 text-sm text-left hover:bg-gray-50"
                        >
                          For Sale
                        </button>
                        <button
                          onClick={() => handleStatusDropdownChange("loan")}
                          className="w-full px-3 py-2 text-sm text-left hover:bg-gray-50"
                        >
                          For Loan
                        </button>
                        <button
                          onClick={() =>
                            handleStatusDropdownChange("available")
                          }
                          className="w-full px-3 py-2 text-sm text-left hover:bg-gray-50"
                        >
                          Mark as available
                        </button>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={handleConfirmStatusChange}
                    className="text-blue-500 hover:text-blue-700 p-1"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </button>
                  <button
                    onClick={handleCancelStatusChange}
                    className="text-gray-500 hover:text-gray-700 p-1"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              ) : uiState === "show-input" ? (
                <div className="relative">
                  <input
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 pr-16 border border-[#374DF5] rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder={`Enter ${
                      editType === "sale" ? "transfer" : "loan"
                    } fee`}
                  />
                  <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
                    <button
                      onClick={handleConfirmFee}
                      className="text-blue-500 hover:text-blue-700 p-1"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </button>
                    <button
                      onClick={handleCancelInput}
                      className="text-gray-500 hover:text-gray-700 p-1"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              ) : uiState === "show-add-fee-button" ||
                uiState === "show-fee-display" ? (
                <div className="flex items-center gap-2">
                  <div className="flex-1 relative" ref={dropdownRef}>
                    <button
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      className="w-full px-3 py-2 text-sm border border-[#E6E6E6] rounded-lg hover:bg-gray-50 flex items-center justify-between"
                    >
                      <span>
                        {selectedStatus === "sale" ||
                        (player.forSale && !selectedStatus)
                          ? "For Sale"
                          : "For Loan"}
                      </span>
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                    {isDropdownOpen && (
                      <div className="absolute top-full left-0 w-full mt-1 bg-white border border-[#E6E6E6] rounded-lg shadow-lg z-10">
                        <button
                          onClick={() => handleStatusDropdownChange("sale")}
                          className="w-full px-3 py-2 text-sm text-left hover:bg-gray-50"
                        >
                          For Sale
                        </button>
                        <button
                          onClick={() => handleStatusDropdownChange("loan")}
                          className="w-full px-3 py-2 text-sm text-left hover:bg-gray-50"
                        >
                          For Loan
                        </button>
                        <button
                          onClick={() =>
                            handleStatusDropdownChange("available")
                          }
                          className="w-full px-3 py-2 text-sm text-left hover:bg-gray-50"
                        >
                          Mark as available
                        </button>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={
                      uiState === "show-fee-display"
                        ? handleEditFee
                        : handleAddFeeButtonClick
                    }
                    className="flex-1 px-3 py-2 text-sm text-[#1E1E1E] border border-[#E6E6E6] rounded-lg hover:bg-gray-50 flex items-center justify-between"
                  >
                    <span>{getButtonLabel()}</span>
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M11.3333 1.99955C11.5083 1.82445 11.7162 1.68556 11.945 1.5908C12.1738 1.49604 12.419 1.44727 12.6666 1.44727C12.9142 1.44727 13.1594 1.49604 13.3882 1.5908C13.617 1.68556 13.8248 1.82445 13.9999 1.99955C14.175 2.17465 14.3139 2.38252 14.4087 2.61129C14.5034 2.84006 14.5522 3.08526 14.5522 3.33288C14.5522 3.58051 14.5034 3.8257 14.4087 4.05448C14.3139 4.28325 14.175 4.49112 13.9999 4.66622L4.99992 13.6662L1.33325 14.6662L2.33325 10.9996L11.3333 1.99955Z"
                        stroke="#2C2C2C"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <div className="flex-1 relative" ref={dropdownRef}>
                    <button
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      className="w-full px-3 py-2 text-sm border border-[#E6E6E6] rounded-lg hover:bg-gray-50 flex items-center justify-between"
                    >
                      <span>{player.forSale ? "For Sale" : "For Loan"}</span>
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                    {isDropdownOpen && (
                      <div className="absolute top-full left-0 w-full mt-1 bg-white border border-[#E6E6E6] rounded-lg shadow-lg z-10">
                        <button
                          onClick={() => handleStatusDropdownChange("sale")}
                          className="w-full px-3 py-2 text-sm text-left hover:bg-gray-50"
                        >
                          For Sale
                        </button>
                        <button
                          onClick={() => handleStatusDropdownChange("loan")}
                          className="w-full px-3 py-2 text-sm text-left hover:bg-gray-50"
                        >
                          For Loan
                        </button>
                        <button
                          onClick={() =>
                            handleStatusDropdownChange("available")
                          }
                          className="w-full px-3 py-2 text-sm text-left hover:bg-gray-50"
                        >
                          Mark as available
                        </button>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      const type = player.forSale ? "sale" : "loan";
                      setEditType(type);
                      const existingFee =
                        type === "sale"
                          ? saleAmount || player.transferFee?.toString() || ""
                          : loanAmount || player.loanFee?.toString() || "";
                      setInputValue(existingFee);
                      setUiState("show-input");
                    }}
                    className="flex-1 px-3 py-2 text-sm text-[#1E1E1E] border border-[#E6E6E6] rounded-lg hover:bg-gray-50 flex items-center justify-between"
                  >
                    <span>{getButtonLabel()}</span>
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M11.3333 1.99955C11.5083 1.82445 11.7162 1.68556 11.945 1.5908C12.1738 1.49604 12.419 1.44727 12.6666 1.44727C12.9142 1.44727 13.1594 1.49604 13.3882 1.5908C13.617 1.68556 13.8248 1.82445 13.9999 1.99955C14.175 2.17465 14.3139 2.38252 14.4087 2.61129C14.5034 2.84006 14.5522 3.08526 14.5522 3.33288C14.5522 3.58051 14.5034 3.8257 14.4087 4.05448C14.3139 4.28325 14.175 4.49112 13.9999 4.66622L4.99992 13.6662L1.33325 14.6662L2.33325 10.9996L11.3333 1.99955Z"
                        stroke="#2C2C2C"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              )}
            </>
          ) : uiState === "show-input" ? (
            <div className="relative">
              <input
                type="text"
                value={inputValue}
                onChange={handleInputChange}
                className="w-full px-3 py-2 pr-16 border border-[#374DF5] rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder={`Enter ${
                  editType === "sale" ? "transfer" : "loan"
                } fee`}
              />
              <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
                <button
                  onClick={handleConfirmFee}
                  className="text-blue-500 hover:text-blue-700 p-1"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </button>
                <button
                  onClick={handleCancelInput}
                  className="text-gray-500 hover:text-gray-700 p-1"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>
          ) : uiState === "show-add-fee-button" ? (
            <button
              onClick={handleAddFeeButtonClick}
              className="w-full px-3 py-2 text-sm text-[#1E1E1E] border border-[#E6E6E6] rounded-lg hover:bg-gray-50 flex items-center justify-between"
            >
              <span>
                {editType === "sale"
                  ? "Add Max. Transfer fee"
                  : "Add Max. Loan fee"}
              </span>
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11.3333 1.99955C11.5083 1.82445 11.7162 1.68556 11.945 1.5908C12.1738 1.49604 12.419 1.44727 12.6666 1.44727C12.9142 1.44727 13.1594 1.49604 13.3882 1.5908C13.617 1.68556 13.8248 1.82445 13.9999 1.99955C14.175 2.17465 14.3139 2.38252 14.4087 2.61129C14.5034 2.84006 14.5522 3.08526 14.5522 3.33288C14.5522 3.58051 14.5034 3.8257 14.4087 4.05448C14.3139 4.28325 14.175 4.49112 13.9999 4.66622L4.99992 13.6662L1.33325 14.6662L2.33325 10.9996L11.3333 1.99955Z"
                  stroke="#2C2C2C"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          ) : uiState === "show-fee-display" ? (
            <button
              onClick={handleEditFee}
              className="w-full px-3 py-2 text-sm text-[#1E1E1E] border border-[#E6E6E6] rounded-lg hover:bg-gray-50 flex items-center justify-between"
            >
              <span>
                {editType === "sale" ? "Max Transfer fee" : "Max Loan fee"}: €{" "}
                {editType === "sale"
                  ? saleAmount || formatGroupedNumber(player.transferFee) || ""
                  : loanAmount || formatGroupedNumber(player.loanFee) || ""}
              </span>
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11.3333 1.99955C11.5083 1.82445 11.7162 1.68556 11.945 1.5908C12.1738 1.49604 12.419 1.44727 12.6666 1.44727C12.9142 1.44727 13.1594 1.49604 13.3882 1.5908C13.617 1.68556 13.8248 1.82445 13.9999 1.99955C14.175 2.17465 14.3139 2.38252 14.4087 2.61129C14.5034 2.84006 14.5522 3.08526 14.5522 3.33288C14.5522 3.58051 14.5034 3.8257 14.4087 4.05448C14.3139 4.28325 14.175 4.49112 13.9999 4.66622L4.99992 13.6662L1.33325 14.6662L2.33325 10.9996L11.3333 1.99955Z"
                  stroke="#2C2C2C"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          ) : (
            <div className="flex gap-2">
              <button
                onClick={handleAddToSaleClick}
                className="flex-1 px-3 py-2 text-sm text-[#1E1E1E] border border-[#E6E6E6] rounded-lg hover:bg-gray-50"
              >
                Add to Sale
              </button>
              <button
                onClick={handleAddToLoanClick}
                className="flex-1 px-3 py-2 text-sm text-[#1E1E1E] border border-[#E6E6E6] rounded-lg hover:bg-gray-50"
              >
                Add to Loan
              </button>
            </div>
          )}
        </div>
      )}

      {/* Show initial state when card is cancelled */}
      {isQuickEdit && isCardCancelled && (
        <div className="mt-4">
          <div className="flex gap-2">
            <button
              onClick={handleAddToSaleClick}
              className="flex-1 px-3 py-2 text-sm text-[#1E1E1E] border border-[#E6E6E6] rounded-lg hover:bg-gray-50"
            >
              Add to Sale
            </button>
            <button
              onClick={handleAddToLoanClick}
              className="flex-1 px-3 py-2 text-sm text-[#1E1E1E] border border-[#E6E6E6] rounded-lg hover:bg-gray-50"
            >
              Add to Loan
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlayerCard;
